import { Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle, Instagram, Music2, Heart, Layers, Users, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { CONTACT, SITE_URL } from "@/content";

const values = [
  { icon: Layers, title: "Fokus auf Anime & TCG", text: "Pokémon und One Piece bilden unseren Schwerpunkt – ergänzt um Yu-Gi-Oh!, Zubehör, Anime-Figuren und Merch. Alles, was Sammlerherzen höher schlagen lässt." },
  { icon: Users, title: "Community im Mittelpunkt", text: "Wir sind mehr als ein Laden: Bei Trade Days und Events triffst du andere Fans, tauschst Karten und wirst Teil der Kölner TCG-Community." },
  { icon: Heart, title: "Von Sammlern für Sammler", text: "Wir lieben das Hobby selbst – darum beraten wir ehrlich, bewerten beim Ankauf fair und behandeln jede Sammlung mit Respekt." },
];

export default function UeberUns() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Über uns", item: `${SITE_URL}/ueber-uns` },
    ],
  };

  return (
    <div data-testid="ueber-uns-page">
      <Seo
        title="Über uns | Shibuya Anime & TCG Store Köln"
        description="Lerne den Shibuya Anime & TCG Store in Köln kennen: Wer wir sind, warum wir den Store betreiben und was dich bei Pokémon, One Piece, Anime-Figuren & Community-Events erwartet."
        path="/ueber-uns"
        keywords="Shibuya Store Köln, Über uns Anime Store Köln, TCG Store Köln, Card Shop Köln, Pokémon Store Köln Team"
      />
      <JsonLd id="bc-ueber-uns" data={breadcrumbSchema} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="hero-glow absolute inset-0" />
        <div className="bg-grid absolute inset-0" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-6 top-0 select-none font-jp text-[140px] font-black leading-none text-white/[0.05] md:text-[220px]">渋谷</div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
          <nav data-testid="breadcrumb" className="flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
            <Link to="/" className="transition-colors hover:text-foreground">Start</Link>
            <ChevronRight className="h-3 w-3" /> <span className="text-primary">Über uns</span>
          </nav>
          <Reveal>
            <Badge variant="outline" className="mt-6 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-primary">
              Tokyo Style in Köln
            </Badge>
            <h1 className="mt-5 max-w-3xl font-display text-4xl uppercase leading-[0.95] tracking-[0.04em] sm:text-5xl lg:text-6xl">
              Über den Shibuya Anime &amp; TCG Store
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Der Shibuya Anime &amp; TCG Store ist dein Treffpunkt für Trading Card
              Games, Anime-Figuren und Community – mitten in Köln, im Tokyo-Style.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 md:py-20">
        <Reveal>
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              Wir haben den Shibuya Store gegründet, weil uns genau ein Ort in Köln
              gefehlt hat: ein Laden, in dem sich alles um Anime und Trading Card
              Games dreht – zum Anfassen, Stöbern und gemeinsam Erleben. Benannt nach
              dem berühmten Tokioter Stadtviertel Shibuya bringen wir ein Stück
              japanische Pop-Kultur in die Domstadt.
            </p>
            <p>
              Bei uns stehen <span className="text-foreground">Pokémon</span> und{" "}
              <span className="text-foreground">One Piece</span> im Mittelpunkt:
              Booster, Displays, Einzelkarten und Sealed-Produkte. Dazu kommen
              Yu-Gi-Oh!, hochwertiges TCG-Zubehör, detailreiche Anime-Figuren und
              ausgewählter Merch. Was du suchst, kannst du vor Ort prüfen und direkt
              mitnehmen – ohne Wartezeit, ohne Versandrisiko.
            </p>
            <p>
              Genauso wichtig wie das Sortiment ist uns die Community. Bei Trade Days
              und Events triffst du andere Sammler und Spieler, tauschst Karten und
              lernst neue Leute kennen. Und wenn du Karten abgeben möchtest, kaufen
              wir fair an und zahlen sofort aus – in bar oder als Store-Gutschein.
            </p>
          </div>
        </Reveal>
      </section>

      {/* VALUES */}
      <section className="border-y border-border bg-card/50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="neon-card h-full rounded-2xl border border-border bg-card p-6">
                  <v.icon className="h-6 w-6 text-primary" />
                  <p className="mt-4 font-display text-2xl uppercase tracking-wide">{v.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + SOCIAL */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div className="noise relative overflow-hidden rounded-2xl border border-border bg-card p-8 text-center md:p-12">
            <div className="cta-glow pointer-events-none absolute inset-0" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl uppercase tracking-[0.04em] sm:text-4xl">
                Komm vorbei – <span className="neon-text">mitten in Köln.</span>
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
                {CONTACT.addressStreet}, {CONTACT.addressCity}. Folge uns auch auf
                Social Media für Neuheiten, Drops und Event-Termine.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
                  <a href={CONTACT.phoneHref}><Phone className="mr-2 h-5 w-5" /> {CONTACT.phoneDisplay}</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 rounded-xl border-primary/30 bg-transparent px-6 hover:bg-primary/10">
                  <Link to="/anfahrt"><MapPin className="mr-2 h-5 w-5" /> Anfahrt</Link>
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="ueber-instagram" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
                  <Instagram className="h-4 w-4 text-primary" /> Instagram
                </a>
                <a href={CONTACT.tiktok} target="_blank" rel="noopener noreferrer" data-testid="ueber-tiktok" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
                  <Music2 className="h-4 w-4 text-primary" /> TikTok
                </a>
              </div>
              <div className="mt-8">
                <Link to="/pokemon-karten-koeln" className="link-slide inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                  Unser Sortiment entdecken <ArrowRight className="h-4 w-4 text-primary" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
